<script lang="ts">
  import { afterUpdate } from "svelte";
  import { communitySlug } from "app/stores/community";
  import BasicSearchEntry from "./BasicSearchEntry.svelte";
  import { docURL, replyUrl, threadURL } from "app/utils/urls";
  import { slugify } from "app/utils/strings";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import {
    CheckFilled,
    DashedCircle,
    Doc,
    Reply,
    ReplyTo,
    Right,
    Squircle,
  } from "$components/+blocks/OVIcon";
  import { isDarkTheme } from "app/stores/userPreferences";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import { keyboardNavigate } from "app/actions/keyboardNavigate";
  import type { Results } from "./BasicSearch.svelte";

  export let results: Results;

  $: resultsLength = results.docs.length + results.threads.length + results.replies.length;

  let ref: HTMLDivElement;
  let resultsDivHeight: number;

  // array for references to result elements
  // needed for keyboard navigation to handle focus
  let resultsRefs: HTMLElement[] = [];

  afterUpdate(() => {
    // set the height of the results div, based on the content, but not more than 800px and not more than distance to the bottom of the window
    const distanceToBottomWindow = window.innerHeight - ref.getBoundingClientRect().top - 100;
    resultsDivHeight = Math.min(ref.scrollHeight, distanceToBottomWindow, 800);

    // slice the resultsRefs array to the length of the results
    resultsRefs = resultsRefs.slice(0, resultsLength);
  });
</script>

<div
  class="cubic-bezier(.67,.66,.55,.93) scrollbar-thin overflow-y-auto [mask-image:_linear-gradient(to_bottom,_transparent_-1%,_black_1%),_linear-gradient(to_top,_transparent_-1%,_black_3%)] [mask-composite:intersect] transition-[height] duration-[175ms] scrollbar-stable"
  style={`height: ${resultsDivHeight}px;`}
>
  <div
    bind:this={ref}
    use:keyboardNavigate={resultsRefs}
    role={"list"}
    class="box-content flex flex-col gap-6 px-3"
    class:pt-6={resultsLength}
    class:pb-2.5={resultsLength}
  >
    {#if results.docs.length}
      <div class="flex flex-col gap-3">
        <h3 class="text-xs font-medium text-neutral-500 dark:text-neutral-400">Docs</h3>
        {#each results.docs as doc, index (doc.uuid)}
          <BasicSearchEntry
            icon={Doc}
            title={doc.highlightedTitle}
            description={doc.highlightedText}
            link={docURL({
              communitySlug: $communitySlug,
              docUuid: doc.uuid,
              docSlug: slugify(doc.title),
            })}
            bind:ref={resultsRefs[index]}
          >
            <svelte:fragment slot="breadcrumbs">
              {#if doc.section}
                <span class="max-w-[25ch] truncate">
                  {doc.section}
                </span>
                <OVIcon icon={Right} class="text-neutral-400 dark:text-neutral-400" size="size-3" />
              {/if}
              {#if doc.folder}
                <span class="max-w-[25ch] truncate">
                  {doc.folder}
                </span>
                <OVIcon icon={Right} class="text-neutral-400 dark:text-neutral-400" size="size-3" />
              {/if}
              <span>Doc</span>
            </svelte:fragment>
          </BasicSearchEntry>
        {/each}
      </div>
    {/if}

    {#if results.threads.length}
      <div class="flex flex-col gap-3">
        <h3 class="text-xs font-medium text-neutral-500 dark:text-neutral-400">Threads</h3>
        {#each results.threads as thread, index (thread.uuid)}
          {@const icon = thread.forumSpace.solvableThreads
            ? thread.solution
              ? CheckFilled
              : DashedCircle
            : Reply}
          <BasicSearchEntry
            {icon}
            iconColour={icon === CheckFilled ? "text-emerald-400" : "text-neutral-400"}
            title={thread.highlightedTitle}
            description={thread.highlightedText}
            link={threadURL({
              communitySlug: $communitySlug,
              spaceUuid: thread.forumSpace.uuid,
              spaceSlug: slugify(thread.forumSpace.title),
              threadUuid: thread.uuid,
              threadSlug: slugify(thread.title),
            })}
            bind:ref={resultsRefs[index + results.docs.length]}
          >
            <svelte:fragment slot="breadcrumbs">
              {#if thread.forumSpace.icon}
                {@const icon =
                  $isDarkTheme && thread.forumSpace.iconDark
                    ? thread.forumSpace.iconDark
                    : thread.forumSpace.icon}
                <Avatar mainImage={icon} size="size-3.5" radius="rounded-[3px]" />
              {:else}
                <span style="color: {thread.forumSpace.color}">
                  <OVIcon icon={Squircle} class="shrink-0" size="size-3.5" />
                </span>
              {/if}
              <span class="max-w-[25ch] truncate">
                {thread.forumSpace.title}
              </span>
              <OVIcon icon={Right} class="text-neutral-400 dark:text-neutral-400" size="size-3" />
              <span>Thread</span>
            </svelte:fragment>
          </BasicSearchEntry>
        {/each}
      </div>
    {/if}

    {#if results.replies.length}
      <div class="flex flex-col gap-3">
        <h3 class="text-xs font-medium text-neutral-500 dark:text-neutral-400">Replies</h3>
        {#each results.replies as reply, index (reply.seqNum + reply.thread.uuid)}
          <BasicSearchEntry
            icon={ReplyTo}
            title={reply.thread.title}
            description={reply.highlightedText}
            link={replyUrl({
              communitySlug: $communitySlug,
              spaceUuid: reply.thread.forumSpace.uuid,
              spaceSlug: slugify(reply.thread.forumSpace.title),
              threadUuid: reply.thread.uuid,
              threadSlug: slugify(reply.thread.title),
              replySeqNum: reply.seqNum,
            })}
            bind:ref={resultsRefs[index + results.docs.length + results.threads.length]}
          >
            <svelte:fragment slot="breadcrumbs">
              {#if reply.thread.forumSpace.icon}
                {@const icon =
                  $isDarkTheme && reply.thread.forumSpace.iconDark
                    ? reply.thread.forumSpace.iconDark
                    : reply.thread.forumSpace.icon}
                <Avatar mainImage={icon} size="size-3.5" radius="rounded-[3px]" />
              {:else}
                <span style="color: {reply.thread.forumSpace.color}">
                  <OVIcon icon={Squircle} class="shrink-0" size="size-3.5" />
                </span>
              {/if}
              <span class="max-w-[25ch] truncate">
                {reply.thread.forumSpace.title}
              </span>
              <OVIcon icon={Right} class="text-neutral-400 dark:text-neutral-400 " size="size-3" />
              <span class="max-w-[25ch] truncate">{reply.thread.title}</span>
              <OVIcon icon={Right} class="text-neutral-400 dark:text-neutral-400" size="size-3" />
              <span>Reply</span>
            </svelte:fragment>
          </BasicSearchEntry>
        {/each}
      </div>
    {/if}
  </div>
</div>
